import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Select, Input, Combobox, Autocomplete } from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import {
  storeTanquesVirtual,
  updateTanquesVirtual,
} from '~/store/modules/tanquevirtual/actions';

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleLoading,
}) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.tanquevirtual);
  const [pendingRequests, setPendingRequests] = useState(0);

  const initials = create ? data : { ...find, ...data };

  const handleAsyncLoadingChange = useCallback((isLoading) => {
    setPendingRequests((state) => {
      if (isLoading) return state + 1;

      return state > 0 ? state - 1 : 0;
    });
  }, []);

  async function handleSubmit(param) {
    try {
      if (pendingRequests > 0) {
        toast.warning(t('s.geral.validacao.aguardecarregamento'));
        return;
      }

      const schema = Yup.object().shape({
        idtanquevirtual: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(11, t('s.geral.validacao.tamanhomaximo').replace('%s', 11))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        idposto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(11, t('s.geral.validacao.tamanhomaximo').replace('%s', 11))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        idproduto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(30, t('s.geral.validacao.tamanhomaximo').replace('%s', 30))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        statustanquevirtual: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(1, t('s.geral.validacao.tamanhomaximo').replace('%s', 1)),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
      };

      if (create) {
        await dispatch(storeTanquesVirtual(objSend, params));
      } else {
        await dispatch(updateTanquesVirtual(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading && reference.current) {
      reference.current.setData(initials);
    }
  }, [loading, initials, reference]);

  useEffect(() => {
    toggleLoading(pendingRequests > 0);
  }, [pendingRequests, toggleLoading]);

  useEffect(
    () => () => {
      toggleLoading(false);
    },
    [toggleLoading]
  );

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <div style={{ position: 'relative', minHeight: '14rem' }}>
          <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
            <Input
              name="idtanquevirtual"
              label={t('s.cadastro.tanquevirtual.idtanquevirtual')}
              disabled={!create}
            />

            <Autocomplete
              name="idposto"
              label="codigonomeposto"
              url="postoscomboios"
              unique="idposto"
              title={t('s.geral.postocomboio')}
              disabled={!create}
              onLoadingChange={handleAsyncLoadingChange}
            />

            <Autocomplete
              name="idproduto"
              label="nomeproduto"
              url="produtos"
              unique="idproduto"
              title={t('s.menu.configuracao.produto')}
              onLoadingChange={handleAsyncLoadingChange}
            />

            <Select
              name="statustanquevirtual"
              variant="outlined"
              label={t('s.cadastro.tanquevirtual.statustanquevirtual')}
              options={[
                { value: 'L', label: t('s.geral.opcoes.liberado') },
                { value: 'B', label: t('s.geral.opcoes.bloqueado') },
                { value: 'C', label: t('s.geral.opcoes.cancelado') },
              ]}
            />
          </Form>
          <Spinner loading={pendingRequests > 0} />
        </div>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleLoading: PropTypes.func,
};

CreateOrUpdate.defaultProps = {
  data: {},
  toggleLoading: () => {},
};
